import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { onboardingActions } from 'src/store/actions';

import CONSTANTS from 'src/store/constants';

import { Col, Container, Div, Icon, Modal, Row, Text } from '@pinpoint/design-kit';

import { Page } from 'src/containers';
import { ROUTES } from 'src/config';
import { Switch, Button } from 'src/components';

function ReOnboarding() {
  const [isOpen, showConfirmModal] = useState(false);
  const [compState, setCompState] = useState(CONSTANTS.COMPONENT.VALIDATION.PENDING);
  const query = new URLSearchParams(useLocation().search);
  const uploadFileId = query.get('upload_file_id');
  const history = useHistory();

  const dispatch = useDispatch();
  /* eslint-disable no-shadow */
  const getOnboardingStatus = useSelector(({ onboarding: { getOnboardingStatus } }) => getOnboardingStatus);

  const onboarding = useSelector(({ onboarding: { onboarding } }) => onboarding);

  const tenant = useSelector(
    ({
      app: {
        profile: { tenant },
      },
    }) => tenant
  );

  /* eslint-enable no-shadow */

  // Fetch Information about the onboarding on page load
  useEffect(() => {
    dispatch(onboardingActions.getUploadFile(uploadFileId));
  }, [tenant, dispatch, uploadFileId]);

  // Validate based on the state of the onboarding
  useEffect(() => {
    if (getOnboardingStatus === CONSTANTS.API.SUCCEEDED && onboarding) {
      // check if onboarding is in the `DET_WAITING_FOR_CONFIRMATION` state
      const sortedStates = onboarding.states.sort((a, b) => b.id - a.id);
      if (sortedStates.length > 0) {
        const { state: latestState } = sortedStates[0];
        if (CONSTANTS.REONBOARDABLE_STATES.includes(latestState)) {
          setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
        } else {
          // Check if re-onboaardable states was an historic state
          const hasState = sortedStates.find(({ state }) => CONSTANTS.REONBOARDABLE_STATES.includes(state));
          if (hasState) {
            setCompState(CONSTANTS.COMPONENT.VALIDATION.SUPERSEDED);
          } else {
            setCompState(CONSTANTS.COMPONENT.VALIDATION.FAILED);
          }
        }
      } else {
        setCompState(CONSTANTS.COMPONENT.VALIDATION.ERROR);
      }
    } else if (getOnboardingStatus === CONSTANTS.API.FAILED) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.ERROR);
    }
  }, [getOnboardingStatus, onboarding]);

  useLayoutEffect(() => {
    if (compState === CONSTANTS.COMPONENT.VALIDATION.VALIDATED) {
      showConfirmModal(true);
      setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
    }
  }, [compState]);

  return (
    <Container>
      <Modal
        align="center"
        rounded="md"
        isOpen={isOpen}
        onClose={() => {
          showConfirmModal(false);
        }}
      >
        <Icon
          name="Cross"
          pos="absolute"
          top="1rem"
          right="1rem"
          size="16px"
          onClick={() => {
            showConfirmModal(false);
          }}
          cursor="pointer"
        />
        <Div d="flex" flexDir="column" justify="space-between">
          <Div d="flex" align="center" m={{ b: '4rem' }}>
            <Icon name="AlertSolid" color="warning700" m={{ r: '1rem' }} size="3rem" />
            <Text textSize="subheader">Do you want to re-onboard a new file?</Text>
          </Div>
          <Div d="flex" justify="flex-end">
            <Button
              buttonStyle="secondary"
              onClick={() => {
                showConfirmModal(false);
                setCompState(CONSTANTS.COMPONENT.OPERATION.READY);
              }}
            >
              CANCEL
            </Button>
            <Button
              buttonStyle="primary"
              onClick={() => {
                showConfirmModal(false);
                // history.push to ROUTES.NEW_ONBOARDING
                history.push(ROUTES.NEW_ONBOARDING, {
                  datasetName: onboarding.onboarding.dataset_name,
                  datasetTag: onboarding.onboarding.dataset_tag,
                  onboardingType: onboarding.onboarding.type,
                  onboardingPayload: onboarding.onboarding.payload,
                  onboardingId: onboarding.onboarding.id,
                });
              }}
            >
              YES
            </Button>
          </Div>
        </Div>
      </Modal>

      <Row d="flex" m={{ x: '-0.25rem', t: '5rem', b: '1.25rem' }}>
        <Col size="12" gutterWidth="1.25rem">
          <Div
            bg="white"
            rounded="md"
            p={{ x: '1.5rem', y: '1.25rem' }}
            w="100%"
            textColor="shade33"
            d="flex"
            justify="space-between"
            align="flex-start"
          >
            <Div d="flex" flexDir="column" justify="space-between" align="flex-start">
              <Text textSize="title" textColor="tabSelected" m={{ y: '0.5rem' }}>
                Confirmation
              </Text>
              <Switch status={compState}>
                <Div value={CONSTANTS.COMPONENT.VALIDATION.PENDING} d="flex" align="center">
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    Loading... Please wait!
                  </Div>
                </Div>
                <Div value={CONSTANTS.COMPONENT.OPERATION.STARTED} d="flex" align="center">
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    Please click &ldquo;Yes&rdquo; in the pop up to proceed with the re-onboarding
                  </Div>
                </Div>
                <Div value={CONSTANTS.COMPONENT.OPERATION.SUCCEEDED} d="flex" align="center">
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    The re-onboarding is all set to proceed!
                  </Div>
                </Div>
                <Div
                  value={[
                    CONSTANTS.COMPONENT.VALIDATION.VALIDATED,
                    CONSTANTS.COMPONENT.OPERATION.READY,
                    CONSTANTS.COMPONENT.OPERATION.FAILED,
                  ]}
                  d="flex"
                  align="center"
                >
                  <Button
                    buttonStyle="primary"
                    onClick={() => {
                      showConfirmModal(true);
                      setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
                    }}
                  >
                    Click Here
                  </Button>
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    to proceed with the Onboarding
                  </Div>
                </Div>
                <Div value={CONSTANTS.COMPONENT.VALIDATION.SUPERSEDED} d="flex" align="center">
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    The file has already been re-onboarded.
                  </Div>
                </Div>
                <Div value={CONSTANTS.COMPONENT.VALIDATION.FAILED} d="flex" align="center">
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    Something went wrong. Are you sure you got the right onboarding?
                  </Div>
                </Div>
                <Div value={CONSTANTS.COMPONENT.VALIDATION.ERROR} d="flex" align="center">
                  <Div textSize="subheader" m={{ l: '0.5rem' }} textColor="tabSelected">
                    There is a problem when loading your onboarding. Please try again later and if the problem persists
                    please contact support.
                  </Div>
                </Div>
              </Switch>
            </Div>
          </Div>
        </Col>
      </Row>
    </Container>
  );
}

export default Page(ReOnboarding);
